/**
 * New Project wizard contract (2026-07-29 client meeting + Brandon's voice note).
 *
 * Coffman wins a second job and needs it routable today: a project row, a
 * system From-address so Approve & Send can send, memberships so the crew that
 * runs 25132 can see it, and — the part that makes the wizard worth building —
 * the roster carried across so nobody re-types 60 employees.
 *
 * Two rules the whole shape follows:
 *
 *  1. **Rates are a point-in-time snapshot, never a link.** Per-project rosters
 *     were confirmed as the right architecture at the meeting. Copying writes
 *     new rows; editing the source project later never touches the copy.
 *  2. **Rates that differ per job must be verified by a human.** Equipment
 *     rates are copied at their CURRENT value (never zeroed — Brandon was
 *     explicit: a zeroed rate silently bills nothing, which is worse than a
 *     wrong one you can see), and the wizard leaves a persistent reminder on
 *     the new project so the verification can't be forgotten. Employee rates
 *     are the opposite case: the wizard makes you type them, so they need no
 *     reminder.
 *
 * Lives in its own module rather than api.ts so the wizard's surface is
 * reviewable on its own.
 */

/** Project-level config on the new project. Same field semantics as the
 *  Project Details settings page — this is that form, before the project
 *  exists. */
export interface NewProjectDetails {
  /** HeavyJob job number. UNIQUE across projects — the routing key every
   *  inbound PDF resolves through, so a clash is a 409, never a second row. */
  project_number: string;
  name: string;
  gc_name: string;
  /** Primary Approve & Send recipient. */
  gc_email: string;
  pm_name: string;
  pm_email: string;
  /** Null when the contract value isn't known yet — the PCO Log strip renders
   *  it as absent rather than $0. */
  original_contract_amount_cents: number | null;
  markup_pct: number;
  equipment_tax_pct: number;
  /** Project DAY default. Employees seed from it; step 2 overrides per-copy. */
  default_st_rate_cents: number;
  /** Project SWING default — the source of truth for swing billing. */
  default_swing_rate_cents: number;
  per_diem_rate_cents: number;
  default_per_diem_eligible: boolean;
  /**
   * True bills ST/OT/DT at their multipliers. False (the 25132 shape) bills
   * total worked hours flat at the ST rate. Named for the toggle the client
   * asked for — "Calculate OT & DT" — and inverted onto the stored
   * `bill_overtime_as_st` column by the server.
   */
  calculate_ot_dt: boolean;
  /**
   * True bills Sunday hours at their own premium rate instead of the day
   * rate. Config, not money — it carries from the source project, the way
   * `calculate_ot_dt` does.
   */
  sunday_premium_enabled: boolean;
  /**
   * The Sunday rate itself. Null when the job has none. A RATE, so it never
   * carries from another project — the same reason the day and swing
   * defaults start blank. Required (and above zero) when the toggle is on:
   * the pricing helper needs both, and a switched-on premium with no rate
   * would silently bill Sundays at the day rate.
   */
  sunday_premium_rate_cents: number | null;
}

/**
 * What to carry over from the source project. Every flag defaults to false —
 * an all-false selection creates a bare project, which is exactly what the
 * ops-only admin route has always done.
 */
export interface NewProjectCopySelections {
  /**
   * Copy every ACTIVE employee. heavyjob ids, names, classifications and
   * per_diem_eligible carry as-is; both rates are REPLACED by the two values
   * below, because Coffman bills a composite rate per job and the old job's
   * rate is never the new job's rate.
   */
  employees: boolean;
  /** Applied to every copied employee's st_rate_cents. Required when
   *  `employees` is true. */
  employee_st_rate_cents?: number;
  /** Applied to every copied employee's swing_rate_cents. Required when
   *  `employees` is true. */
  employee_swing_rate_cents?: number;
  /**
   * Copy every ACTIVE equipment item AT ITS CURRENT RATE. Sets
   * `equipment_rates_review_note` on the new project, which Settings →
   * Equipment renders as an amber banner until dismissed.
   */
  equipment: boolean;
  /** Copy the materials master with its default rates. No persistent banner —
   *  the wizard's copy carries the one-line verify nudge instead. */
  materials: boolean;
}

/**
 * The config the wizard's details step prefills from the chosen copy source.
 *
 * RATES are absent on purpose. The day, swing and Sunday-premium rates start
 * blank whichever job is picked, because the old job's rate is never the new
 * job's — the same rule that makes step 2 type the employee rates by hand.
 */
export interface CopySourceDefaults {
  gc_name: string;
  gc_email: string;
  pm_name: string;
  pm_email: string;
  markup_pct: number;
  equipment_tax_pct: number;
  per_diem_rate_cents: number;
  default_per_diem_eligible: boolean;
  calculate_ot_dt: boolean;
  sunday_premium_enabled: boolean;
}

/** ACTIVE rows only — what copying would actually write. */
export interface CopySourceRosterCounts {
  employees: number;
  equipment: number;
  materials: number;
}

/** One row of the wizard's Copy from pick list. */
export interface CopySource {
  project_id: string;
  project_number: string;
  name: string;
  roster_counts: CopySourceRosterCounts;
  defaults: CopySourceDefaults;
}

/** GET /api/projects/copy-sources — every job the caller is a member of. */
export interface CopySourcesResponse {
  sources: CopySource[];
}

/**
 * The pick list's order: the job the caller is standing in first, then by job
 * number, numerically where both numbers are numeric.
 *
 * One implementation for the server's response order and the web's own
 * re-order (which still has to happen — the fallback list is built locally when
 * the fetch fails), so the list can't read one way in a test and another on
 * screen. Returns a new array; the input is left alone.
 */
export function orderCopySources<T extends { project_id: string; project_number: string }>(
  sources: T[],
  currentProjectId: string,
): T[] {
  return [...sources].sort((a, b) => {
    if (a.project_id === currentProjectId) return b.project_id === currentProjectId ? 0 : -1;
    if (b.project_id === currentProjectId) return 1;
    return compareProjectNumbers(a.project_number, b.project_number);
  });
}

/**
 * Job numbers as a human reads them: 25132 before 26122 before 26123, and
 * "9999" before "26122" rather than after it, which a plain string compare
 * gets wrong. Non-numeric numbers (none today, but the column is text) fall
 * back to a string compare and sort after the numeric ones.
 *
 * Exported because the cross-job rate comparison orders its COLUMNS the same
 * way but has no "current job first" rank to apply — the reader is comparing
 * jobs, not standing in one.
 */
export function compareProjectNumbers(a: string, b: string): number {
  const na = /^\d+$/.test(a.trim()) ? Number(a.trim()) : null;
  const nb = /^\d+$/.test(b.trim()) ? Number(b.trim()) : null;
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** POST /api/projects/new — body. */
export interface CreateProjectRequest {
  details: NewProjectDetails;
  copy: NewProjectCopySelections;
  /**
   * Which job to copy from. Absent means the project the caller is standing
   * in, which is what the wizard sent before the source became a choice.
   *
   * The caller must hold a `project_users` row on it — naming another job
   * would otherwise read its crew and its rates. The system From-address is
   * NOT taken from here: it comes from the caller's current project, because a
   * project's sender has to be a domain Resend has verified, not a free choice.
   */
  source_project_id?: string;
}

/** What actually got written, so the wizard's success step can be specific
 *  rather than "done". */
export interface CreateProjectCopyCounts {
  employees: number;
  equipment: number;
  materials: number;
  /** Memberships granted on the new project, mirrored from the source's. */
  members: number;
}

export interface CreateProjectResponse {
  project_id: string;
  project_number: string;
  /** The purpose='system' From-address row created alongside the project. */
  from_address_id: string;
  copied: CreateProjectCopyCounts;
  /**
   * Present when the project itself was created but the roster copy died
   * partway (copies deliberately sit outside the atomic batch — a half-copied
   * roster is recoverable in Settings, a half-created project is not). The
   * wizard shows this instead of pretending the create failed: a retry would
   * 409 on a number that "looks unused" while the project quietly exists.
   */
  copy_warning?: string;
}

/**
 * Failure codes. `project_number_exists` is the one the wizard surfaces inline
 * on the project-number field (both the soft pre-check and the unique index
 * produce it); the rest are form-level.
 */
export type CreateProjectErrorCode =
  | 'project_number_exists'
  | 'missing_required_fields'
  | 'invalid_email'
  | 'invalid_field_types'
  | 'employee_rates_required'
  | 'forbidden_not_admin'
  /**
   * A named `source_project_id` the caller holds no membership on. Distinct
   * from the `project_forbidden` that `requireAuth` answers with, and
   * deliberately so: the web treats that one as "your stored project pick went
   * stale", clears the pick and reloads. This is a bad pick INSIDE an open
   * wizard, and the recovery would throw the admin out of the form.
   */
  | 'source_forbidden'
  /** A named `source_project_id` that no project has. */
  | 'source_not_found'
  | 'project_create_failed';

export interface CreateProjectErrorResponse {
  error: CreateProjectErrorCode;
  /** Present on `missing_required_fields` / `invalid_field_types` /
   *  `invalid_email` — which fields. */
  fields?: string[];
  /** Present on `project_number_exists`. */
  project_number?: string;
  project_id?: string | null;
  message?: string;
}

/**
 * PATCH /api/settings/project/equipment-review-note — the Dismiss on the amber
 * banner. Nulls `projects.equipment_rates_review_note`; there is no way to set
 * it from the UI, only the wizard writes it.
 */
export interface DismissEquipmentReviewNoteResponse {
  ok: true;
}

/**
 * The note the wizard stamps on a project whose equipment came from another
 * job. Free text so it reads as prose in the banner, but generated from one
 * place so every project's reads the same.
 */
export function equipmentRatesReviewNote(
  sourceProjectNumber: string,
  when: Date = new Date(),
): string {
  const y = when.getUTCFullYear();
  const m = String(when.getUTCMonth() + 1).padStart(2, '0');
  const d = String(when.getUTCDate()).padStart(2, '0');
  return `Rates copied from ${sourceProjectNumber} · ${y}-${m}-${d}`;
}
